import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Sidebar from "../components/Sidebar";
import HelpButton from "../components/HelpButton";
import QrSheetModal from "../components/QrSheet";
import { endpoints } from "../apiConfig";
import { colors, fonts, space, radius, card, buttonPrimary, buttonSecondary, inputBase, pageHeading } from "../theme";
import { getAuthHeader, getLoginRedirect, getBusinessName } from "../session";
import { getWageToken, wageHeader } from "../wageUnlock";
import WageUnlockModal from "../components/WageUnlockModal";

// Employees = the people who clock in. Separate from Staff (waiter / kitchen / manager PIN logins),
// though an employee can optionally be linked to one staff account.
export default function EmployeesPage() {
  const [data, setData] = useState(null);
  const [moduleOff, setModuleOff] = useState(false);
  const [businessName, setBusinessName] = useState("");
  const [name, setName] = useState("");
  const [pin, setPin] = useState("");
  const [staffId, setStaffId] = useState("");
  const [shared, setShared] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({});
  const [copied, setCopied] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [showWageUnlock, setShowWageUnlock] = useState(false);
  const authHeader = () => ({ ...getAuthHeader(), ...wageHeader() });

  const load = () => {
    if (!getAuthHeader().Authorization) { window.location.href = getLoginRedirect(); return; }
    axios.get(endpoints.employees, { headers: authHeader() })
      .then((res) => { setData(res.data); setModuleOff(false); })
      .catch((err) => {
        if (err.response?.data?.code === "module_disabled") setModuleOff(true);
        else if (err.response?.status === 401 || err.response?.status === 403) window.location.href = getLoginRedirect();
      });
    setBusinessName(getBusinessName());
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, []);

  const act = async (fn) => {
    setError("");
    try { await fn(); load(); return true; } catch (err) { setError(err.response?.data?.error || "That didn't work. Please try again."); return false; }
  };

  const add = (e) => {
    e.preventDefault();
    act(async () => {
      await axios.post(endpoints.employees, { name, pin_code: pin, staff_id: staffId || null, shared_pin: shared && !!staffId }, { headers: authHeader() });
      setName(""); setPin(""); setStaffId(""); setShared(false);
    });
  };
  const patch = (id, body) => act(() => axios.patch(endpoints.employee(id), body, { headers: authHeader() }));
  const remove = (emp) => {
    if (!window.confirm(`Delete ${emp.name}?`)) return;
    act(() => axios.delete(endpoints.employee(emp.id), { headers: authHeader() }));
  };
  const openEdit = (emp) => {
    setEditing(emp.id);
    setDraft({
      name: emp.name, pin_code: "", staff_id: emp.staff_id || "", shared_pin: emp.shared_pin,
      hire_date: emp.hire_date || "", phone: emp.phone || "", email: emp.email || "", address: emp.address || "",
      pay_type: emp.pay_type || "", hourly_rate: emp.hourly_rate ?? "", salary_amount: emp.salary_amount ?? "", salary_period: emp.salary_period || "",
    });
  };
  const saveEdit = async (emp) => {
    const body = {
      name: draft.name, staff_id: draft.staff_id || null, shared_pin: !!draft.staff_id && !!draft.shared_pin,
      hire_date: draft.hire_date || "", phone: draft.phone || "", email: draft.email || "", address: draft.address || "",
    };
    if (draft.pin_code) body.pin_code = draft.pin_code;
    if (data.wage_unlocked) {
      body.pay_type = draft.pay_type || "";
      body.hourly_rate = draft.pay_type === "hourly" ? draft.hourly_rate : "";
      body.salary_amount = draft.pay_type === "salaried" ? draft.salary_amount : "";
      body.salary_period = draft.pay_type === "salaried" ? draft.salary_period : "";
    }
    if (await patch(emp.id, body)) setEditing(null);
  };
  const unlockWage = () => setShowWageUnlock(true);
  const copy = () => { navigator.clipboard?.writeText(data.clock_url); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  const capReached = data && data.active_count >= data.max_employees;
  const staffOptions = (forEmployee) => (data?.staff || []).filter((s) => !s.employee_id || s.employee_id === forEmployee);

  if (moduleOff) {
    return (
      <div style={{ display: "flex", background: colors.bgPage, minHeight: "100vh" }}>
        <Sidebar businessName={businessName} />
        <div className="app-content" style={{ flex: 1, padding: space[6], maxWidth: 720 }}>
          <h1 style={pageHeading}>Employees</h1>
          <div style={{ ...card, marginTop: space[4] }}>
            <p style={{ fontFamily: fonts.body, color: colors.textSecondary, marginTop: 0 }}>
              The Employee module (clock in/out with GPS and a photo, hours history and payroll export) isn't turned on for your restaurant yet.
            </p>
            <Link to="/settings" style={{ ...buttonPrimary, textDecoration: "none", display: "inline-block" }}>Request it under Settings</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", background: colors.bgPage, minHeight: "100vh" }}>
      <Sidebar businessName={businessName} />
      <div className="app-content" style={{ flex: 1, padding: space[6], maxWidth: 900 }}>
        <h1 style={pageHeading}>Employees <HelpButton page="employees" /></h1>

        {data && (
          <>
            <div style={styles.counter} data-testid="employee-counter">{data.active_count} of {data.max_employees} employees active</div>
            <div style={styles.wageBar} data-testid="wage-unlock-bar">
              {data.wage_unlocked ? (
                <span style={styles.wageOn}>🔓 Wage information is unlocked for this visit.</span>
              ) : (
                <>
                  <span>🔒 Wage information (pay type, rate, salary) is hidden.</span>
                  <button style={styles.wageLinkBtn} onClick={unlockWage} data-testid="unlock-wage-button">Enter password to view</button>
                </>
              )}
            </div>
            {capReached && (
              <div style={styles.capMessage} data-testid="employee-cap-message">
                Limit reached: max {data.max_employees} employees. Upgrade to add more:{" "}
                <Link to="/settings" style={{ color: "inherit", fontWeight: 600 }}>request more under Settings</Link>.
              </div>
            )}

            <div style={{ ...card, marginTop: space[3] }}>
              <div style={{ fontFamily: fonts.display, fontWeight: 600, marginBottom: 4 }}>Clock-in page</div>
              <p style={styles.hint}>Share this link (or print the QR code) with your employees. They enter their name and PIN, and a photo and GPS are taken at the restaurant.</p>
              <div className="inline-form" style={{ display: "flex", gap: space[2], alignItems: "center", flexWrap: "wrap" }}>
                <input readOnly value={data.clock_url} style={{ ...inputBase, flex: "1 1 260px", minWidth: 0, fontSize: 12 }} onFocus={(e) => e.target.select()} aria-label="Clock-in link" />
                <button style={buttonSecondary} onClick={copy}>{copied ? "Copied!" : "Copy"}</button>
                <button style={buttonSecondary} onClick={() => setShowQr(true)}>Print QR code</button>
              </div>
            </div>

            <form className="inline-form" onSubmit={add} style={{ ...styles.inlineForm, marginTop: space[4] }}>
              <input style={styles.input} placeholder="Employee name" value={name} onChange={(e) => setName(e.target.value)} required aria-label="Employee name" />
              <input style={{ ...styles.input, width: 130 }} placeholder="Clock-in PIN (4 to 6 digits)" inputMode="numeric" maxLength={6} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} required aria-label="Clock-in PIN" />
              <select style={styles.input} value={staffId} onChange={(e) => { setStaffId(e.target.value); if (!e.target.value) setShared(false); }} aria-label="Link to staff account">
                <option value="">No staff link</option>
                {staffOptions(0).filter((s) => s.is_active).map((s) => <option key={s.id} value={s.id}>{s.name} ({s.role})</option>)}
              </select>
              <button style={{ ...buttonPrimary, padding: "8px 14px", opacity: capReached ? 0.5 : 1 }} type="submit" disabled={capReached}>Add Employee</button>
            </form>
            {staffId && (
              <label style={styles.checkRow}>
                <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} />
                Use the same PIN for the staff login (one number for both)
              </label>
            )}
            <p style={styles.hint}>
              Linking an employee to a staff account makes that staff account view-only while the employee is clocked out
              (when “Require clock-in” is on under Attendance). Leave it unlinked for an employee who doesn't use the Waiter or Kitchen screens.
            </p>
            {error && <div style={styles.error} role="alert">{error}</div>}

            {data.employees.length === 0 ? (
              <p style={{ fontFamily: fonts.body, color: colors.textSecondary }}>No employees yet.</p>
            ) : (
              <div style={styles.list}>
                {data.employees.map((emp) => (
                  <div key={emp.id} style={styles.row} data-testid="employee-row">
                    {editing === emp.id ? (
                      <div style={{ width: "100%" }}>
                        <div className="inline-form" style={styles.inlineForm}>
                          <input style={styles.input} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} aria-label="Name" />
                          <input style={{ ...styles.input, width: 150 }} placeholder="New PIN (optional)" inputMode="numeric" maxLength={6} value={draft.pin_code} onChange={(e) => setDraft({ ...draft, pin_code: e.target.value.replace(/\D/g, "") })} aria-label="New PIN" />
                          <select style={styles.input} value={draft.staff_id} onChange={(e) => setDraft({ ...draft, staff_id: e.target.value, shared_pin: e.target.value ? draft.shared_pin : false })} aria-label="Staff link">
                            <option value="">No staff link</option>
                            {staffOptions(emp.id).map((s) => <option key={s.id} value={s.id}>{s.name} ({s.role})</option>)}
                          </select>
                          {draft.staff_id && (
                            <label style={styles.checkRow}><input type="checkbox" checked={!!draft.shared_pin} onChange={(e) => setDraft({ ...draft, shared_pin: e.target.checked })} /> Same PIN for staff login</label>
                          )}
                        </div>
                        <div className="inline-form" style={{ ...styles.inlineForm, marginTop: space[2] }}>
                          <label style={styles.field}>Hiring date<input style={styles.fieldInput} type="date" value={draft.hire_date} onChange={(e) => setDraft({ ...draft, hire_date: e.target.value })} aria-label="Hiring date" /></label>
                          <label style={styles.field}>Phone<input style={styles.fieldInput} value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} aria-label="Phone number" /></label>
                          <label style={styles.field}>Email<input style={styles.fieldInput} type="email" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} aria-label="Email" /></label>
                          <label style={{ ...styles.field, flex: "2 1 240px" }}>Address<input style={styles.fieldInput} value={draft.address} onChange={(e) => setDraft({ ...draft, address: e.target.value })} aria-label="Address" /></label>
                        </div>
                        {data.wage_unlocked ? (
                          <div className="inline-form" style={{ ...styles.inlineForm, marginTop: space[2] }} data-testid="wage-fields">
                            <select style={styles.input} value={draft.pay_type} onChange={(e) => setDraft({ ...draft, pay_type: e.target.value })} aria-label="Pay type">
                              <option value="">No pay type set</option>
                              <option value="hourly">Hourly</option>
                              <option value="salaried">Salaried</option>
                            </select>
                            {draft.pay_type === "hourly" && (
                              <label style={styles.field}>Hourly rate<input style={styles.fieldInput} type="number" step="0.01" min="0" value={draft.hourly_rate} onChange={(e) => setDraft({ ...draft, hourly_rate: e.target.value })} aria-label="Hourly rate" /></label>
                            )}
                            {draft.pay_type === "salaried" && (
                              <>
                                <label style={styles.field}>Salary amount<input style={styles.fieldInput} type="number" step="0.01" min="0" value={draft.salary_amount} onChange={(e) => setDraft({ ...draft, salary_amount: e.target.value })} aria-label="Salary amount" /></label>
                                <select style={styles.input} value={draft.salary_period} onChange={(e) => setDraft({ ...draft, salary_period: e.target.value })} aria-label="Salary period">
                                  <option value="">Paid how often?</option>
                                  <option value="weekly">Weekly</option>
                                  <option value="biweekly">Biweekly</option>
                                  <option value="monthly">Monthly</option>
                                </select>
                              </>
                            )}
                          </div>
                        ) : (
                          <button type="button" style={styles.wageLinkBtn} onClick={unlockWage}>🔒 Enter password to set pay</button>
                        )}
                        <div style={{ display: "flex", gap: space[2], marginTop: space[3] }}>
                          <button style={{ ...buttonPrimary, padding: "8px 14px" }} onClick={() => saveEdit(emp)}>Save</button>
                          <button style={buttonSecondary} onClick={() => setEditing(null)}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div style={{ flex: "1 1 200px", minWidth: 0 }}>
                          <div style={{ fontWeight: 600, wordBreak: "break-word" }}>{emp.name}</div>
                          <div style={styles.sub}>
                            {emp.is_active ? "Active" : "Inactive"}
                            {emp.clocked_in && <span style={styles.inBadge}>Clocked in</span>}
                          </div>
                          <div style={styles.sub}>
                            {emp.staff_id ? `Linked to ${emp.staff_name} (${emp.staff_role}), ${emp.shared_pin ? "one shared PIN" : "two separate PINs"}` : "Not linked to a staff account"}
                          </div>
                          {(emp.hire_date || emp.phone || emp.email || emp.address) && (
                            <div style={styles.sub}>
                              {[emp.hire_date && `Hired ${emp.hire_date}`, emp.phone, emp.email, emp.address].filter(Boolean).join(" · ")}
                            </div>
                          )}
                          {data.wage_unlocked && emp.pay_type && (
                            <div style={styles.sub} data-testid="wage-summary">
                              {emp.pay_type === "hourly" ? `$${Number(emp.hourly_rate).toFixed(2)}/hr` : `$${Number(emp.salary_amount).toFixed(2)} ${emp.salary_period}`}
                            </div>
                          )}
                        </div>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          <button style={styles.smallBtn} onClick={() => openEdit(emp)}>Edit</button>
                          <button style={styles.smallBtn} onClick={() => patch(emp.id, { is_active: !emp.is_active })}>{emp.is_active ? "Deactivate" : "Activate"}</button>
                          <button style={{ ...styles.smallBtn, color: "#b91c1c" }} onClick={() => remove(emp)}>Delete</button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {showQr && data && (
          <QrSheetModal
            businessName={businessName}
            title="Print clock-in QR code"
            intro="One code that opens the clock-in page. Put it by the door or the till. Employees scan it, then enter their name and PIN."
            subtitle="Clock in / out"
            instructions="Scan with your phone camera, then enter your name and PIN."
            links={[{ key: "clock", label: "Clock In / Out", url: data.clock_url }]}
            onClose={() => setShowQr(false)}
          />
        )}
        {showWageUnlock && (
          <WageUnlockModal onUnlock={() => { setShowWageUnlock(false); load(); }} onClose={() => setShowWageUnlock(false)} />
        )}
      </div>
    </div>
  );
}

const styles = {
  counter: { fontFamily: fonts.body, fontSize: 13, color: colors.textSecondary, marginTop: space[2] },
  wageBar: { display: "flex", alignItems: "center", gap: space[2], flexWrap: "wrap", fontFamily: fonts.body, fontSize: 12, color: colors.textSecondary, marginTop: space[2] },
  wageOn: { color: "#166534" },
  wageLinkBtn: { background: "none", border: "none", color: colors.brandBlue, fontSize: 12, fontFamily: fonts.body, cursor: "pointer", padding: 0, fontWeight: 600 },
  field: { display: "flex", flexDirection: "column", gap: 4, fontFamily: fonts.body, fontSize: 11, color: colors.textMuted, flex: "1 1 150px" },
  fieldInput: { ...inputBase, width: "100%", boxSizing: "border-box" },
  capMessage: { fontFamily: fonts.body, fontSize: 12, color: "#92400e", background: "#fef3c7", padding: space[2], borderRadius: radius.sm, marginTop: space[2] },
  hint: { fontFamily: fonts.body, fontSize: 12, color: colors.textMuted, margin: `${space[1]} 0` },
  inlineForm: { display: "flex", gap: space[2], flexWrap: "wrap", alignItems: "center" },
  input: { ...inputBase, flex: "1 1 160px", minWidth: 0 },
  checkRow: { display: "flex", alignItems: "center", gap: 8, fontFamily: fonts.body, fontSize: 13, color: colors.textPrimary, margin: `${space[2]} 0` },
  error: { fontFamily: fonts.body, fontSize: 13, color: "#b91c1c", margin: `${space[2]} 0` },
  list: { marginTop: space[3], background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md },
  row: { display: "flex", gap: space[3], justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", padding: space[3], borderBottom: `1px solid ${colors.border}`, fontFamily: fonts.body, fontSize: 14, color: colors.textPrimary },
  sub: { fontSize: 12, color: colors.textSecondary, marginTop: 2 },
  inBadge: { marginLeft: 8, background: "#dcfce7", color: "#166534", borderRadius: 999, padding: "1px 8px", fontSize: 11, fontWeight: 600 },
  smallBtn: { padding: "6px 10px", fontSize: 12, borderRadius: radius.sm, border: `1px solid ${colors.border}`, background: colors.surfaceMuted, cursor: "pointer" },
};
